/**
 * Sprint M5.35 (29.07.2026) — PowerPaket-In-App-Bereich (Stub).
 *
 * Fuer User mit `powerpaket_lifetime === true`. Zeigt den geplanten
 * Inhalts-Rahmen des PowerPakets an. Echte Lehrmodule folgen Q4.
 *
 * Sprachprinzip Flex4Success v8: „PowerPaket", Sanduhr/Radar/Schluessel,
 * Alltagssprache, keine Anglizismen.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { Sparkles, Package, Lock, ArrowRight, Clock, Book, Wrench, Compass } from "lucide-react";

const PLANNED_MODULES = [
  {
    id: "modul-1",
    title: "Die vier Primärfokustypen im Alltag",
    subline: "Lehr-Video · Q4 2026",
    icon: Compass,
  },
  {
    id: "modul-2",
    title: "Deine Sanduhr — Momente, in denen sich alles entscheidet",
    subline: "Lehr-Video · Q4 2026",
    icon: Clock,
  },
  {
    id: "modul-3",
    title: "Werkzeugkasten — 12 Bausteine für den nächsten Flexpfad",
    subline: "Praxis-Baukasten · Q4 2026",
    icon: Wrench,
  },
  {
    id: "modul-4",
    title: "Das Arbeitsheft — 60 Seiten zum Mitschreiben",
    subline: "PDF · Q4 2026",
    icon: Book,
  },
];

export default function DashboardPowerPaket() {
  const navigate = useNavigate();
  return (
    <DashboardLayout>
      {({ data }) => {
        const user = data.user;
        const hasPowerPaket = Boolean(
          user?.powerpaket_lifetime === true || user?.powerpaket_granted_at,
        );

        return (
          <div
            className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10"
            data-testid="dashboard-powerpaket-page"
          >
            <header className="mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-bold uppercase tracking-wider">
                <Sparkles className="w-3.5 h-3.5" />
                PowerPaket
              </div>
              <h1
                className="mt-4 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white"
                data-testid="powerpaket-headline"
              >
                {hasPowerPaket
                  ? "Dein PowerPaket wartet auf dich."
                  : "Das PowerPaket — ein Blick hinein."}
              </h1>
              <p className="mt-3 text-base text-slate-600 dark:text-slate-300 max-w-2xl">
                {hasPowerPaket
                  ? "Wir bauen gerade die vollen Inhalte. Sobald ein Modul fertig ist, findest du es hier — automatisch."
                  : "Vier vertiefende Bausteine, mit denen du die Primärfokustypen im Alltag anwendest."}
              </p>
            </header>

            {hasPowerPaket && (
              <div
                className="mb-8 rounded-2xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 p-5 flex items-start gap-3"
                data-testid="powerpaket-status-active"
              >
                <Package className="w-5 h-5 text-emerald-700 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-emerald-900 dark:text-emerald-200">
                    PowerPaket freigeschaltet.
                  </p>
                  <p className="text-sm text-emerald-800 dark:text-emerald-300 mt-1">
                    Dein Zugang ist dauerhaft. Wir schreiben dich an, sobald ein neues Modul verfügbar ist.
                  </p>
                </div>
              </div>
            )}

            {!hasPowerPaket && (
              <div
                className="mb-8 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 flex items-start gap-3"
                data-testid="powerpaket-status-locked"
              >
                <Lock className="w-5 h-5 text-slate-500 dark:text-slate-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-bold text-slate-900 dark:text-white">
                    Noch nicht freigeschaltet.
                  </p>
                  <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                    Hol dir das PowerPaket im Launch-Angebot beim Jahresplan (kostenfrei dazu) oder als
                    Einzel-Kauf für 197 €.
                  </p>
                  <Link
                    to="/pricing"
                    data-testid="powerpaket-cta-pricing"
                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-[#4563BB] dark:text-blue-300 hover:underline"
                  >
                    Zu den Plänen <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            )}

            <section aria-labelledby="powerpaket-modules-heading">
              <h2
                id="powerpaket-modules-heading"
                className="text-sm font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-4"
              >
                Die vier geplanten Bausteine
              </h2>
              <ol className="space-y-3" data-testid="powerpaket-modules-list">
                {PLANNED_MODULES.map((m, i) => {
                  const Icon = m.icon;
                  return (
                    <li
                      key={m.id}
                      data-testid={`powerpaket-module-${m.id}`}
                      className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 sm:p-5 flex items-start gap-4"
                    >
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                          Baustein {i + 1}
                        </p>
                        <h3 className="mt-1 text-base sm:text-lg font-bold text-slate-900 dark:text-white">
                          {m.title}
                        </h3>
                        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{m.subline}</p>
                      </div>
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[11px] font-bold uppercase tracking-wider whitespace-nowrap">
                        <Lock className="w-3 h-3" /> Bald
                      </span>
                    </li>
                  );
                })}
              </ol>
            </section>

            <p
              className="mt-8 text-xs text-slate-400 dark:text-slate-500"
              data-testid="powerpaket-honest-note"
            >
              Ehrlicher Hinweis: Die vollen Inhalte kommen im vierten Quartal 2026.
              Wir schreiben dich an, sobald das erste Modul freigeschaltet ist.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => navigate("/cockpit")}
                data-testid="powerpaket-back-cockpit"
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                Zurück in den Mitgliederbereich
              </button>
            </div>
          </div>
        );
      }}
    </DashboardLayout>
  );
}
